import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { default as Input } from 'antd/es/input';
import type { ChangeEventHandler, KeyboardEventHandler } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Location } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';

import { useOrganisationId } from 'hooks/context';

import { useLocalState } from 'components/pages/Search/localState';

import { SEARCH_BY_REF_TEST_ID } from './constants';

const searchPageRegex = /^\/org\/\d+\/search/;

export const SearchWidget = () => {
  const navigate = useNavigate();
  const location = useLocation() as Location & {
    state: { from: string | undefined };
  };
  const currentPath = useRef<string>(location.pathname);
  const isSearchPage = searchPageRegex.test(location.pathname);

  const { localState } = useLocalState();
  const [fromPath, setFromPath] = useState<string>();

  const organisationId = useOrganisationId();
  const [searchValue, setSearchValue] = useState('');

  // Update redirection path
  useEffect(() => {
    if (searchPageRegex.test(currentPath.current) && !isSearchPage) {
      setFromPath(undefined);
    } else if (location.state?.from) {
      setFromPath(location.state.from);
    }
    currentPath.current = location.pathname;
  }, [isSearchPage, location.pathname, location.state?.from]);

  // Update component search value state
  useEffect(() => {
    if (isSearchPage && localState.q) {
      setSearchValue(localState.q);
    } else if (!isSearchPage) {
      setSearchValue('');
    }
  }, [location, localState, isSearchPage]);

  const onChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = event.target.value;
    setSearchValue(value);
    if (value === '') {
      if (fromPath) {
        return navigate(fromPath);
      } else if (isSearchPage) {
        return navigate(`/org/${organisationId}`);
      }
    }
  };

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    const value = event.currentTarget.value.trim();
    if (event.key == 'Enter' && value) {
      const state = isSearchPage ? undefined : { from: location };
      navigate(`/org/${organisationId}/search?q=${value}`, {
        state
      });
    }
  };

  return (
    <Input
      prefix={
        <MagnifyingGlassIcon className="absolute left-2 m-2.5 size-6 stroke-1" />
      }
      data-testid={SEARCH_BY_REF_TEST_ID}
      size="large"
      name="search"
      value={searchValue}
      title="Search"
      placeholder="Search ref. or end-to-end id"
      className="cool-shadow w-84 p-2.5 pl-12"
      onKeyDown={onKeyDown}
      onChange={onChange}
    />
  );
};
